/** Scene configuration. Each planet's physical constants plus the high-level
 *  details of its ring (and, later, moons): how big the planet is, and how many
 *  asteroids sit where. The detailed ring *generation* — mineral mix, size and
 *  radial distributions, orbit seeding — lives in ring.ts; this file only
 *  declares the knobs a designer reaches for first. */

export interface RingSpec {
  /** Total asteroids in the ring. Almost all live as dormant proxies; only the
   *  handful inside the active zone are ever full bodies — see ring.ts. */
  count: number;
  /** Orbital radius of the ring's centre, in metres from the planet centre. */
  centerM: number;
  /** Radial half-width of the band, in metres. Asteroids bell-cluster toward
   *  the centre and feather out to either side by this distance. */
  halfWidthM: number;
  /** Inclusive range of block counts each asteroid is built from. */
  minPieces: number;
  maxPieces: number;
  /** Seed for the deterministic ring layout. */
  seed: number;
}

export interface PlanetSpec {
  name: string;
  /** Surface radius, metres. */
  radiusM: number;
  /** Surface gravity, m/s² — the source of truth for the planet's mass. */
  surfaceGravity: number;
  /** Ships spawn this far beyond the ring's outer edge (metres), so they don't
   *  need a hole punched in the ring to fit. */
  shipRingGapM: number;
  ring: RingSpec;
}

/** μ = G·M, back-solved from surface gravity via g = μ / R². Drives both
 *  gravity (a = μ/r²) and circular-orbit speed (v = √(μ/r)). */
export function gravParam(p: PlanetSpec): number {
  return p.surfaceGravity * p.radiusM * p.radiusM;
}

/** Player/partner spawn shell: just beyond the ring's outer edge. */
export function playerOrbitRadius(p: PlanetSpec): number {
  return p.ring.centerM + p.ring.halfWidthM + p.shipRingGapM;
}

/** The world's current planet. Swap or extend this to retune the scene. */
export const HOME_PLANET: PlanetSpec = {
  name: "Luna",
  radiusM: 50_000,
  surfaceGravity: 1.62, // lunar
  shipRingGapM: 400,
  ring: {
    count: 10_000,
    centerM: 90_000, // 4× the original 10km orbital altitude off the 50km surface
    halfWidthM: 1_200,
    minPieces: 8,
    maxPieces: 64,
    seed: 1337,
  },
};
